import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Search, Users, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AdminSidebar from '@/components/layout/AdminSidebar';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

export default function TeamManager({ user, setUser }) {
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLeague, setFilterLeague] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [deletingTeam, setDeletingTeam] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    league_id: '',
    logo_url: '',
    captain_name: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [filterLeague]);

  const fetchData = async () => {
    try {
      const [teamsRes, leaguesRes] = await Promise.all([
        axios.get(`${API}/teams`),
        axios.get(`${API}/leagues`)
      ]);
      setTeams(teamsRes.data);
      setLeagues(leaguesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const params = filterLeague !== 'all' ? { league_id: filterLeague } : {};
      const response = await axios.get(`${API}/teams`, { params });
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const handleOpenDialog = (team = null) => {
    if (team) {
      setEditingTeam(team);
      setFormData({
        name: team.name,
        short_name: team.short_name || '',
        league_id: team.league_id,
        logo_url: team.logo_url || '',
        captain_name: team.captain_name || ''
      });
    } else {
      setEditingTeam(null);
      setFormData({
        name: '',
        short_name: '',
        league_id: filterLeague !== 'all' ? filterLeague : '',
        logo_url: '',
        captain_name: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingTeam) {
        const { league_id, ...updateData } = formData;
        await axios.put(`${API}/teams/${editingTeam.team_id}`, updateData);
        toast.success('Team updated successfully');
      } else {
        await axios.post(`${API}/teams`, formData);
        toast.success('Team created successfully');
      }
      setDialogOpen(false);
      fetchTeams();
    } catch (error) {
      console.error('Error saving team:', error);
      toast.error(error.response?.data?.detail || 'Failed to save team');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingTeam) return;

    try {
      await axios.delete(`${API}/teams/${deletingTeam.team_id}`);
      toast.success('Team deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingTeam(null);
      fetchTeams();
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error('Failed to delete team');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, logo_url: response.data.url }));
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const getLeagueName = (leagueId) => {
    const league = leagues.find(l => l.league_id === leagueId);
    return league?.name || 'Unknown League';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminSidebar user={user} setUser={setUser} />
      
      <div className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Teams</h1>
              <p className="text-slate-500 mt-1">Manage teams across leagues</p>
            </div>
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-xl"
              disabled={leagues.length === 0}
              data-testid="create-team-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Team
            </Button>
          </div>

          {/* Filter */}
          <div className="mb-6 max-w-xs">
            <Select value={filterLeague} onValueChange={setFilterLeague}>
              <SelectTrigger className="rounded-xl" data-testid="filter-league">
                <SelectValue placeholder="Filter by league" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Leagues</SelectItem>
                {leagues.map(league => (
                  <SelectItem key={league.league_id} value={league.league_id}>
                    {league.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Teams Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="skeleton h-40 rounded-2xl"></div>
              ))}
            </div>
          ) : leagues.length === 0 ? (
            <div className="empty-state glass-card rounded-2xl">
              <div className="empty-state-icon">
                <Trophy className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Create a league first</h3>
              <p className="text-slate-500 mt-2">You need at least one league before creating teams</p>
            </div>
          ) : teams.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {teams.map((team) => (
                <Card key={team.team_id} className="glass-card rounded-2xl overflow-hidden" data-testid={`team-${team.team_id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      {team.logo_url ? (
                        <img src={team.logo_url} alt={team.name} className="w-14 h-14 rounded-xl object-cover" />
                      ) : (
                        <div className="w-14 h-14 rounded-xl team-logo-placeholder text-xl">
                          {team.short_name?.charAt(0) || team.name.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg text-slate-900 truncate">{team.name}</h3>
                        {team.short_name && (
                          <Badge variant="secondary" className="mt-1">{team.short_name}</Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm text-slate-600 mb-4">
                      <div className="flex items-center gap-2">
                        <Trophy className="w-4 h-4 text-slate-400" />
                        {getLeagueName(team.league_id)}
                      </div>
                      {team.captain_name && (
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-400" />
                          Captain: {team.captain_name}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 rounded-lg"
                        onClick={() => handleOpenDialog(team)}
                        data-testid={`edit-team-${team.team_id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                        onClick={() => {
                          setDeletingTeam(team);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-team-${team.team_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="empty-state glass-card rounded-2xl">
              <div className="empty-state-icon">
                <Users className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">No teams found</h3>
              <p className="text-slate-500 mt-2">Create your first team to get started</p>
              <Button 
                onClick={() => handleOpenDialog()}
                className="mt-4 bg-emerald-900 hover:bg-emerald-800 text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Team
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTeam ? 'Edit Team' : 'Create New Team'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {!editingTeam && (
                <div className="space-y-2">
                  <Label htmlFor="league">League *</Label>
                  <Select 
                    value={formData.league_id} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, league_id: value }))}
                  >
                    <SelectTrigger data-testid="team-league-select">
                      <SelectValue placeholder="Select a league" />
                    </SelectTrigger>
                    <SelectContent>
                      {leagues.map(league => (
                        <SelectItem key={league.league_id} value={league.league_id}>
                          {league.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="name">Team Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., New York Cricket Club"
                  required
                  data-testid="team-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="short_name">Short Name</Label>
                <Input
                  id="short_name"
                  value={formData.short_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, short_name: e.target.value }))}
                  placeholder="e.g., NYCC"
                  data-testid="team-short-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="captain_name">Captain Name</Label>
                <Input
                  id="captain_name"
                  value={formData.captain_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, captain_name: e.target.value }))}
                  placeholder="e.g., John Smith"
                  data-testid="team-captain-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="logo">Team Logo</Label>
                <Input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="cursor-pointer"
                />
                {formData.logo_url && (
                  <div className="mt-2">
                    <img src={formData.logo_url} alt="Preview" className="w-16 h-16 rounded-xl object-cover" />
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-900 hover:bg-emerald-800"
                disabled={submitting || (!editingTeam && !formData.league_id)}
                data-testid="save-team-btn"
              >
                {submitting ? 'Saving...' : (editingTeam ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deletingTeam?.name}" and all associated players. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-team"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
